/**
 * 服务端日志 Facade
 *
 * 统一服务端日志入口，替代直接 import @sentry/cloudflare。
 * 接口与 sentry-logger.ts (客户端) 对齐：trace/debug/info/warn/error/fmt
 *
 * - error/warn 级别必进 Sentry
 * - info 作为 breadcrumb 记录
 * - trace/debug 仅本地开发可见
 * - 本地开发时 fallback 到 console（带 requestId + 上下文）
 *
 * @example
 * ```ts
 * // 无 requestId 场景（cron、queue）
 * import { serverLogger } from "./server-logger";
 * serverLogger.info("[Cron] 开始执行", { job: "daily-snapshot" });
 *
 * // 有 requestId 场景（handler 内）
 * import { createServerLogger } from "./server-logger";
 * const log = createServerLogger(requestId);
 * log.error(new Error("查询失败"), { table: "students" });
 * ```
 */

import * as SentryCloudflare from "@sentry/cloudflare";
import { createError } from "./factories";
import { HTTP_STATUS } from "./http-status";
import { normalizeRuntimeFailure } from "./runtime-failure";
import { isAnySentryHandled, markAllSentryHandled } from "./sentry-dedup";
import { isAppError } from "./type-guards";
import { redactAttributes, redactError, redactString } from "./redaction";

export type ServerLogAttributes = Record<string, unknown>;

const isDev = typeof import.meta !== "undefined" && import.meta.env?.DEV === true;

function consoleLog(level: string, message: string, attrs?: ServerLogAttributes) {
  const rid = attrs?.requestId;
  const prefix = rid ? `[${String(rid)}] ` : "";
  const safeAttrs = attrs ? redactAttributes(attrs) : undefined;
  const suffix = safeAttrs ? ` ${JSON.stringify(safeAttrs)}` : "";
  const formatted = `${prefix}${message}${suffix}`;

  switch (level) {
    case "trace":
    case "debug":
      console.debug(formatted);
      break;
    case "info":
      console.info(formatted);
      break;
    case "warn":
      console.warn(formatted);
      break;
    case "error":
      console.error(formatted);
      break;
    default:
      console.log(formatted);
      break;
  }
}

export function createServerLogger(requestId?: string) {
  const mergeAttrs = (attrs?: ServerLogAttributes): ServerLogAttributes | undefined => {
    if (!(requestId || attrs)) {
      return;
    }
    return { ...(requestId ? { requestId } : {}), ...attrs };
  };

  const requestIdTags = requestId ? { requestId } : undefined;

  return {
    trace(message: string, attrs?: ServerLogAttributes) {
      if (isDev) {
        consoleLog("trace", message, mergeAttrs(attrs));
      }
    },

    debug(message: string, attrs?: ServerLogAttributes) {
      if (isDev) {
        consoleLog("debug", message, mergeAttrs(attrs));
      }
    },

    info(message: string, attrs?: ServerLogAttributes) {
      const merged = redactAttributes(mergeAttrs(attrs));
      const safeMessage = redactString(message);
      if (isDev) {
        consoleLog("info", safeMessage, merged);
      }
      SentryCloudflare.addBreadcrumb({
        message: safeMessage,
        level: "info",
        data: merged,
      });
    },

    warn(message: string, attrs?: ServerLogAttributes) {
      const merged = redactAttributes(mergeAttrs(attrs));
      const safeMessage = redactString(message);
      if (isDev) {
        consoleLog("warn", safeMessage, merged);
      }
      SentryCloudflare.captureMessage(safeMessage, {
        level: "warning",
        tags: requestIdTags,
        extra: merged,
      });
    },

    error(error: unknown, attrs?: ServerLogAttributes) {
      const normalized = normalizeRuntimeFailure(error);
      const exception =
        normalized.failure instanceof Error || isAppError(normalized.failure)
          ? normalized.failure
          : createError.internal("Unknown error type", {
              originalError: normalized.failure,
            });
      const identities =
        exception === normalized.failure
          ? normalized.identities
          : [...normalized.identities, exception];
      const merged = redactAttributes(mergeAttrs(attrs));
      const safeException =
        exception.cause !== undefined || redactString(exception.message) !== exception.message
          ? redactError(exception)
          : exception;
      if (isDev) {
        consoleLog("error", safeException.message, {
          ...merged,
          stack: safeException.stack,
        });
      }

      if (isAnySentryHandled(identities)) {
        return;
      }
      if (isAppError(exception) && exception.statusCode < HTTP_STATUS.INTERNAL_SERVER_ERROR) {
        markAllSentryHandled(identities);
        return;
      }

      const errorCode = merged?.errorCode;
      const statusCode = merged?.statusCode;
      const classificationTags = {
        ...(typeof errorCode === "string" ? { errorCode } : {}),
        ...(typeof statusCode === "number" ? { statusCode: String(statusCode) } : {}),
      };
      const tags =
        requestIdTags || Object.keys(classificationTags).length > 0
          ? { ...requestIdTags, ...classificationTags }
          : undefined;

      SentryCloudflare.captureException(safeException, {
        level: "error",
        tags,
        extra: redactAttributes(attrs),
      });
      markAllSentryHandled(identities);
    },

    fmt(strings: TemplateStringsArray, ...values: unknown[]): string {
      return strings.reduce(
        (result, str, i) => result + str + (values[i] !== undefined ? String(values[i]) : ""),
        "",
      );
    },
  };
}

/** Default server logger without requestId */
export const serverLogger = createServerLogger();
